import { useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import DropDown from '../../../../../../../components/Form/DropDown'
import Label from '../../../../../../../components/Form/Label'
import {
  Note,
  SkillList,
  SkillsWrapper,
  StyledStarFilled,
  StyledStarOutlined,
  StyledTag,
} from './style'

const Skills = ({
  skillList,
  handleSearch,
  skillLoading,
  getListOfSkills,
  disabled,
  skillRefetch,
  roleId,
  roleData
}) => {
  const { values, setFieldValue } = useFormikContext()
  const [searchValue, setSearchValue] = useState('')

  const handleRemoveSkill = skill => {
    setFieldValue(
      'elgibilty.jobRoleSkills',
      (values.elgibilty.jobRoleSkills || []).filter(s => s.id !== skill.id)
    )
  }

  const handleMandatory = skill => {
    setFieldValue(
      'elgibilty.jobRoleSkills',
      (values.elgibilty.jobRoleSkills || []).map(s =>
        s.id === skill.id ? { ...s, isMandatory: !s.isMandatory } : s
      )
    )
  }

  const handleSkillSearch = value => {
    setSearchValue(value)
    //debouncedSearch(value);
    handleSearch(value, 'skill')
  }

  const handleSkillIdSearch = value => {
    // Check if the input is a numeric ID
    if (/^\d+$/.test(value)) {
      setSearchValue(value)
      // For ID search, trigger skillRefetch to get fresh data
      skillRefetch()
      handleSearch(value, 'skillId')
    } else {
      setSearchValue(value)
      handleSearch(value, 'skill')
    }
  }

  const handleSkillsChange = (selectedValues, selectedObjects) => {
    const prevSkills = Array.isArray(values.elgibilty.jobRoleSkills)
      ? values.elgibilty.jobRoleSkills
      : []

    const merged = selectedObjects
      .map(skill => {
        // Find previous skill by skillId (actual skill identifier) or other fields
        const prev = prevSkills.find(
          s =>
            s.skillId === skill.id || // skill.id from dropdown is the actual skillId
            s.value === skill.value ||
            s.id === skill.id ||
            s.name === skill.name
        )

        // Ensure skill has a mandatory name
        const skillName = skill.name || skill.label || skill.value
        if (!skillName) {
       
          return null // Skip skills without names
        }

        // Create skill with proper structure
        const skillWithName = {
          id: skill?.id || `${Date.now()}-${Math.random()}`, // Generate new relationship ID
          skillId: skill?.id, // skill.id from dropdown is the actual skillId
          name: skillName, // Use the validated name
          value: skill?.id, // Use skill.id as value
          isMandatory: prev ? prev.isMandatory : false,
          createdDate: new Date().toISOString(),
          roleId: roleId || null,
        }

        return skillWithName
      })
      .filter(Boolean) // Remove any null skills

    // Validate that all skills have names
    const skillsWithoutNames = merged.filter(skill => !skill.name)
    if (skillsWithoutNames.length > 0) {
    
      // Don't save skills without names
      return
    }
    handleSkillIdSearch('')
    setFieldValue('elgibilty.jobRoleSkills', merged)
  }

  const skillsArray = Array.isArray(values.elgibilty.jobRoleSkills)
    ? values.elgibilty.jobRoleSkills
    : []

  // Function to check if skill exists in skillList and add it
  const checkAndAddSkillById = skillId => {
    if (skillList && Array.isArray(skillList)) {
      const skill = skillList.find(
        s => s.id === parseInt(skillId) || s.id === skillId
      )
      if (skill) {
        const skillWithName = {
          ...skill,
          name: skill.name || skill.value || `Skill ${skill.id}`,
          isMandatory: false,
        }

        // Check if skill already exists
        const exists = skillsArray.some(s => s.id === skill.id)
        if (!exists) {
          setFieldValue('elgibilty.jobRoleSkills', [
            ...skillsArray,
            skillWithName,
          ])
          setSearchValue('')
        } else {
        }
        return true
      }
    }
    return false
  }

  useEffect(() => {
    // Only refetch if we have skills and they're not empty
    if (
      values.elgibilty.jobRoleSkills &&
      values.elgibilty.jobRoleSkills.length > 0
    ) {
      skillRefetch(values.elgibilty.jobRoleSkills)
    }
  }, [values.elgibilty.jobRoleSkills])

  // Check for skills when skillList updates
  useEffect(() => {
    if (
      searchValue &&
      /^\d+$/.test(searchValue) &&
      skillList &&
      skillList.length > 0
    ) {
      // If we're searching by ID and skillList has updated, check if the skill exists
      checkAndAddSkillById(searchValue)
    }
  }, [skillList, searchValue])

  // Fix existing skills data when skillList loads
  useEffect(() => {
    if (skillList && skillList.length > 0 && skillsArray.length > 0) {
      // Map existing skills with full data from skillList
      const updatedSkills = skillsArray.map(existingSkill => {
        // Try different ways to find the skill
        let fullSkill = skillList.find(s => s.id === existingSkill.id)
        if (!fullSkill) {
          fullSkill = skillList.find(s => s.value === existingSkill.id)
        }
        if (!fullSkill) {
          fullSkill = skillList.find(s => s.name === existingSkill.id)
        }

        if (fullSkill) {
          const enhancedSkill = {
            ...existingSkill,
            name:
              fullSkill.name ||
              fullSkill.value ||
              fullSkill.label ||
              `Skill ${fullSkill.id || fullSkill.value}`,
            value:
              fullSkill.value ||
              fullSkill.name ||
              fullSkill.label ||
              `Skill ${fullSkill.id || fullSkill.value}`,
          }
          return enhancedSkill
        }
        return existingSkill
      })

      // Only update if there are actual changes
      const hasChanges = updatedSkills.some(
        (skill, index) =>
          skill.name !== skillsArray[index]?.name ||
          skill.value !== skillsArray[index]?.value
      )

      if (hasChanges) {
        setFieldValue('elgibilty.jobRoleSkills', updatedSkills)
      }
    }
  }, [skillList, skillsArray])

  // Force fix skills display on every render if they're showing as IDs
  const fixedSkillsArray = skillsArray.map(skill => {
    // If skill already has a proper name that's not the ID, use it
    if (
      skill.name &&
      skill.name !== skill.id &&
      skill.name !== `Skill ${skill.id}`
    ) {
      return skill
    }

    // Try to find the skill in skillList to get proper name
    if (skillList && Array.isArray(skillList)) {
      // Try to find by skillId first (this seems to be the actual skill identifier)
      let fullSkill = skillList.find(s => s.id === skill.skillId)
      if (!fullSkill) {
        fullSkill = skillList.find(s => s.id === skill.id)
      }
      if (!fullSkill) {
        fullSkill = skillList.find(s => s.value === skill.id)
      }
      if (!fullSkill) {
        fullSkill = skillList.find(s => s.name === skill.id)
      }

      if (fullSkill) {
        // Use the actual skill name from skillList
        const fixedSkill = {
          ...skill,
          name:
            fullSkill.name ||
            fullSkill.label ||
            fullSkill.value ||
            fullSkill.id,
          value: fullSkill.value || fullSkill.id,
        }
        return fixedSkill
      }
    }

    // If we can't find a proper name, show a readable format
    return {
      ...skill,
      name: `Skill ${skill.id.substring(0, 8)}...`, // Show truncated ID
      value: skill.id,
    }
  })
  useEffect(() => {
   
    if(roleData?.jobRoleSkills?.length > 0){
      setFieldValue('elgibilty.jobRoleSkills',roleData?.jobRoleSkills)
      
    }
  }, [roleData?.jobRoleSkills])

  return (
    <SkillsWrapper>
      <Label>Skills</Label>
      <Note>Click on the star to make the skill mandatory.</Note>
      <DropDown
        options={skillList || []}
        placeholder="Select Skill"
        mode="multiple"
        nameParam="name"
        valueParam="id"
        name="elgibilty.jobRoleSkills"
        value={fixedSkillsArray?.map(skill => skill.skillId || skill.id)}
        onChange={handleSkillsChange}
        showSearch
        onSearch={handleSkillIdSearch}
        loading={skillLoading}
        isFilterOption={false}
        disabled={disabled}
        //isCombined
      />

      {/* Show loader when searching by ID */}
      {skillLoading && /^\d+$/.test(searchValue) && (
        <div
          style={{
            textAlign: 'center',
            padding: '8px',
            color: '#666',
            fontSize: '14px',
          }}
        >
          Searching for skill ID: {searchValue}...
        </div>
      )}

      <SkillList>
      {fixedSkillsArray?.map(skill => (
  <StyledTag
    key={skill.id || skill.name}
    onClose={!disabled ? () => handleRemoveSkill(skill) : undefined}
    closable={!disabled}
    disabled={disabled}
  >
    {skill.name}
    {skill.isMandatory ? (
      <StyledStarFilled
        onClick={!disabled ? () => handleMandatory(skill) : undefined}
        checked={skill.isMandatory}
      />
    ) : (
      <StyledStarOutlined
        onClick={!disabled ? () => handleMandatory(skill) : undefined}
        checked={skill.isMandatory}
      />
    )}
  </StyledTag>
))}

      </SkillList>
    </SkillsWrapper>
  )
}
export default Skills
